import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { dataApi } from '../../api/client'
import { cdApi } from '../../api/changeDetection'
import type { MeshData } from '../../types'
import type { CdDatasetType, CdObservation } from '../../types/changeDetection'

interface Props {
  open: boolean
  projectId: number
  onClose: () => void
  onAdded: (observation: CdObservation) => void
}

export default function CdAddObservationModal({ open, projectId, onClose, onAdded }: Props) {
  const { t } = useTranslation()
  const [candidates, setCandidates] = useState<MeshData[]>([])
  const [meshDataId, setMeshDataId] = useState<number | ''>('')
  const [observedAt, setObservedAt] = useState('')
  const [label, setLabel] = useState('')
  const [datasetType, setDatasetType] = useState<CdDatasetType>('POINT_CLOUD')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setMeshDataId('')
    setObservedAt('')
    setLabel('')
    setDatasetType('POINT_CLOUD')
    setError('')
    dataApi
      .getSuccessful()
      .then((list) => setCandidates(list.filter((d) => d.status === 'SUCCESS')))
      .catch(() => setError(t('cd.obs.modal.loadFailed')))
  }, [open])

  if (!open) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!meshDataId) {
      setError(t('cd.obs.modal.selectRequired'))
      return
    }
    if (!observedAt) {
      setError(t('cd.obs.modal.dateRequired'))
      return
    }
    setSubmitting(true)
    try {
      const obs = await cdApi.addObservation(projectId, {
        meshDataId: Number(meshDataId),
        observedAt,
        label: label.trim() || null,
        datasetType,
      })
      onAdded(obs)
      onClose()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      setError(err.response?.data?.error ?? t('cd.obs.modal.addFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-xl mx-4 bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden text-slate-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <h2 className="text-base font-bold text-white">{t('cd.obs.modal.title')}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-xl leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-slate-300 mb-1.5">{t('cd.obs.modal.date')}</label>
              <input
                type="date"
                value={observedAt}
                onChange={(e) => setObservedAt(e.target.value)}
                className="w-full px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1.5">{t('cd.obs.modal.name')} <span className="text-slate-500">{t('common.optional')}</span></label>
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={t('cd.obs.modal.namePlaceholder')}
                className="w-full px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1.5">{t('cd.obs.modal.dataType')}</label>
            <div className="grid grid-cols-2 gap-3">
              {(['POINT_CLOUD', 'MESH'] as CdDatasetType[]).map((dt) => (
                <button
                  key={dt}
                  type="button"
                  onClick={() => setDatasetType(dt)}
                  className={`py-2.5 rounded-lg text-sm font-medium border transition ${
                    datasetType === dt
                      ? 'bg-blue-950 border-blue-500 text-blue-300'
                      : 'bg-slate-800 border-slate-600 text-slate-400 hover:border-slate-500'
                  }`}
                >
                  {dt === 'POINT_CLOUD' ? t('cd.obs.modal.pointCloud') : t('cd.obs.modal.mesh')}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1.5">{t('cd.obs.modal.converted')}</label>
            <select
              value={meshDataId}
              onChange={(e) => setMeshDataId(e.target.value ? Number(e.target.value) : '')}
              className="w-full px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('cd.obs.modal.selectData')}</option>
              {candidates.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} ({d.createdAt?.slice(0, 10)})
                </option>
              ))}
            </select>
            {candidates.length === 0 && (
              <p className="mt-1 text-xs text-slate-500">{t('cd.obs.modal.noData')}</p>
            )}
          </div>

          {error && <p className="text-xs text-red-400">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800">
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white"
            >
              {submitting ? t('cd.obs.modal.submitting') : t('cd.obs.modal.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
